import {
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  inject,
  input,
  output,
  viewChild
} from '@angular/core';
import {
    UntypedFormControl,
    FormsModule,
    ReactiveFormsModule,
} from '@angular/forms';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { ConfigurationHandlerService } from '../../core/configuration-handler.service';
import { EsploroEntity } from '../../shared/interfaces/esploro-entity';
import { MultiLanguageService } from './multi-language.service';
import { ThemePalette } from '@exl-ng/mulo-common/lib/common-behavior/theme-palette';
import { FilterListPipe } from '../../shared/pipes/filter-list.pipe';
import { HighlightFilterPipe } from '../../shared/pipes/highlight-filter.pipe';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { MatOption, MatOptgroup } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton, MatButton } from '@angular/material/button';
import {
    ScrollableToggleGroupComponent,
    ElementAsHeadingDirective,
} from '@exl-ng/mulo-common';
import { NgClass, AsyncPipe, UpperCasePipe } from '@angular/common';

@Component({
    selector: 'esp-multi-language',
    templateUrl: './multi-language.component.html',
    styleUrls: ['./multi-language.component.scss'],
    imports: [
        ScrollableToggleGroupComponent,
        MatIconButton,
        MatIcon,
        MatButton,
        NgClass,
        MatSelect,
        MatOption,
        NgxMatSelectSearchModule,
        FormsModule,
        ReactiveFormsModule,
        MatOptgroup,
        ElementAsHeadingDirective,
        AsyncPipe,
        UpperCasePipe,
        TranslateModule,
        HighlightFilterPipe,
        FilterListPipe,
    ],
})
export class MultiLanguageComponent implements OnInit {
    private translate = inject(TranslateService);
    multiLanguageService = inject(MultiLanguageService);
    private cnfigurationHandlerService = inject(ConfigurationHandlerService);

    readonly selectButtonComponent = viewChild('selectButton', { read: ElementRef });

    readonly languages = input([]);
    readonly canAdd = input(true);
    readonly addLanguageLabel = input<string>(undefined);
    readonly color = input<ThemePalette>('primary');
    private _initKeys: boolean;
    // TODO: Skipped for migration because:
    //  Accessor inputs cannot be migrated as they are too complex.
    @Input()
    get initKeys() {
        return this._initKeys;
    }

    set initKeys(value) {
        if (value) {
            this.setDefaultSelectedLanguage();
        }
        this._initKeys = value;
    }

    get langLabels() {
        return (
            this.languages()?.map((key) =>
                this.translate.instant('research.lang.' + key),
            ) || []
        );
    }

    readonly languageChanged = output<any>();
    readonly languageRemoved = output<any>();

    selectedLanguage: string;
    hasMultiLanguage: boolean;

    selectedLangCtrl: UntypedFormControl = new UntypedFormControl();
    langFilterCtrl: UntypedFormControl = new UntypedFormControl();

    overlayEl: Element;

    constructor() {
        this.hasMultiLanguage =
            this.cnfigurationHandlerService.isMultiLingualEnabled();
    }

    ngOnInit(): void {}

    setDefaultSelectedLanguage() {
        let key = this.multiLanguageService.defaultLangKey();
        const languages = this.languages();
        if (languages.length === 0) {
            languages.push(key);
        } else if (this.hasMultiLanguage) {
            const hasValue = languages.find((item) => item === key);
            key = hasValue ?? languages[0];
        }
        this._initKeys = false;
        this.languageChange(key);
    }

    languageChange(langCode: string) {
        if (langCode === null || langCode === undefined) return;
        this.selectedLanguage = this.languages().length > 0 ? langCode : '';
        this.languageChanged.emit(langCode);
    }

    filterLanguage = (langCode: EsploroEntity) => {
        const search = this.langFilterCtrl.value?.toLowerCase();
        const translateValue = (val) =>
            this.translate.instant(val)?.toLowerCase();
        return search
            ? translateValue(langCode.code).indexOf(search) !== -1
            : true;
    };

    addLanguage(value) {
        if (!value) {
            return;
        }
        this.languages().push(value);
        this.languageChange(value);
    }

    removeLangComfirmation() {
        this.multiLanguageService
            .showRemoveLanguageConfirmationDialog()
            .subscribe((res) => {
                if (res === 'ok') this.removeLang();
            });
    }

    removeLang() {
        const currIndex = this.languages().findIndex(
            (item) => item === this.selectedLanguage,
        );
        const availableIndex = currIndex - 1 >= 0 ? currIndex - 1 : 0;
        this.languages().splice(currIndex, 1);

        this.languageRemoved.emit(this.selectedLanguage);
        //move to next language in list
        this.languageChange(this.languages()[availableIndex] || '');
    }

    isLangExist(langCode: string) {
        const languages = this.languages();
        if (!languages) return false;
        return languages.some((item) => item === langCode);
    }
}
